import { Channel } from 'stream-chat';
import {
  AVAILABLE_CHANNELS_TO_SHARE,
  CHANNEL_NAMES,
  CHAT_ERROR_MESSAGES,
  CHAT_SUCCESS_MESSAGES,
  CustomMessageField,
  SHARED_DATA_HEADER,
} from '@features/components/Chat/constants';
import { toast } from 'react-toastify';
import {
  ClinicalDataShareMessage,
  OrganApprovalShareMessage,
  QuoteDataShareMessage,
} from '@features/components/Chat/components/ChatContent/Messages/types/chatMessages';
import { useChatContext } from 'stream-chat-react';
import { useCallback } from 'react';
import { PendingShare } from '@features/components/Chat/types';
import { useStreamChatContext } from '@features/contexts/ChatProvider';
import { retrieveUnifiedError } from '@/api/utils';
import { StreamChannelType } from '@/api/apis/chat';

export type MessagePayload =
  | {
      text: string;
      [CustomMessageField.ClinicalDataAttachment]?: ClinicalDataShareMessage;
    }
  | {
      text: string;
      [CustomMessageField.QuoteAttachment]?: QuoteDataShareMessage;
    }
  | {
      text: string;
      [CustomMessageField.OrganApprovalAttachment]?: OrganApprovalShareMessage;
    };

export const useSendSharedMessage = ({
  channels,
  pendingShare,
  setPendingShare,
}: {
  channels: Channel[];
  pendingShare: PendingShare | null;
  setPendingShare: (share: PendingShare | null) => void;
}) => {
  const { client: chatClient, setActiveChannel } = useChatContext();
  const { chatGrants, isOpoMode } = useStreamChatContext();

  const sendMessage = useCallback(
    async (channel: Channel, messagePayload: MessagePayload) => {
      const hasClinicalDataAttachment = CustomMessageField.ClinicalDataAttachment in messagePayload;
      const hasQuoteAttachment = CustomMessageField.QuoteAttachment in messagePayload;
      const hasOrganApprovalAttachment = CustomMessageField.OrganApprovalAttachment in messagePayload;
      const channelType: StreamChannelType = (channel.data?.type as StreamChannelType) ?? StreamChannelType.General;

      if (!hasClinicalDataAttachment && !hasQuoteAttachment && !hasOrganApprovalAttachment) {
        return;
      }

      if (channel?.data?.frozen) {
        setPendingShare(null);
        toast.error(CHAT_ERROR_MESSAGES.frozen);
        return;
      }

      try {
        await channel.sendMessage(messagePayload);
        setActiveChannel(channel);
        setPendingShare(null);
        toast.success(CHAT_SUCCESS_MESSAGES.share(CHANNEL_NAMES[channelType]));
      } catch (err) {
        toast.error(CHAT_ERROR_MESSAGES.general);
      }
    },
    [setActiveChannel, setPendingShare],
  );

  const sendToSpecificChat = useCallback(async () => {
    const shareDataKey: CustomMessageField | null =
      pendingShare && Object.keys(pendingShare).length > 0
        ? (Object.keys(pendingShare)[0] as CustomMessageField)
        : null;

    if (!pendingShare || !chatClient || !shareDataKey || !pendingShare[shareDataKey]) {
      setPendingShare(null);
      return;
    }

    const defaultTargetType = AVAILABLE_CHANNELS_TO_SHARE[shareDataKey];
    const targetType =
      defaultTargetType === StreamChannelType.General && isOpoMode ? StreamChannelType.OpoGeneral : defaultTargetType;

    if (chatGrants && !chatGrants.includes(targetType)) {
      toast.error(CHAT_ERROR_MESSAGES.chatNotFound(CHANNEL_NAMES[targetType]));
      setPendingShare(null);
      return;
    }

    try {
      const targetChannel = channels.find((c) => c.type === targetType);

      if (!targetChannel) {
        toast.error(CHAT_ERROR_MESSAGES.chatNotFound(CHANNEL_NAMES[targetType]));
        setPendingShare(null);
        return;
      }

      await sendMessage(targetChannel, {
        text: SHARED_DATA_HEADER,
        [shareDataKey]: pendingShare[shareDataKey],
      });
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  }, [pendingShare, chatClient, setPendingShare, channels, sendMessage, chatGrants, isOpoMode]);

  return {
    sendSharedDataToSpecificChat: sendToSpecificChat,
    sendMessage,
  };
};
